import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, errorText, type InputStatusDto, type Schemas } from "../api/client";

type Question = Schemas["InputQuestionDto"] | Schemas["PlanQuestionDto"];

/** Rückfragen beantworten. Fehlende Angaben werden nie still ergänzt – auch eine Freigabe ersetzt sie nicht. */
export function Questions({ inputId, questions }: { inputId: string; questions: Question[] }) {
  const open = questions.filter((q) => !q.answer);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [freeText, setFreeText] = useState<Record<string, boolean>>({});
  const qc = useQueryClient();
  const send = useMutation({
    mutationFn: (given: Record<string, string>) =>
      api<InputStatusDto>(`/api/v1/inputs/${inputId}/answers`, {
        body: { answers: Object.entries(given).filter(([, a]) => a.trim()).map(([questionId, answer]) => ({ questionId, answer })) },
      }),
    onSuccess: async () => {
      setAnswers({});
      await qc.invalidateQueries();
    },
    onError: (e) => setError(errorText(e)),
  });
  if (open.length === 0) return null;

  return (
    <div className="questions">
      <h3>Rückfragen</h3>
      {open.map((q) => (
        <div key={q.id} className="question">
          <p>{q.text}</p>
          {q.options.length > 0 && (
            <div className="row wrap">
              {q.options.map((o) => (
                <button key={o} type="button" className={`btn${answers[q.id] === o ? " primary" : ""}`} disabled={send.isPending}
                  onClick={() => {
                    const next = { ...answers, [q.id]: o };
                    setAnswers(next);
                    // Ein Tipp genügt: sind damit alle Rückfragen beantwortet, sofort abschicken.
                    if (open.every((x) => next[x.id]?.trim())) send.mutate(next);
                  }}>
                  {o}
                </button>
              ))}
            </div>
          )}
          {q.options.length === 0 || freeText[q.id] ? (
            <input placeholder="Antwort eingeben" value={answers[q.id] ?? ""} onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} maxLength={2000} />
          ) : (
            <button type="button" className="btn ghost small" onClick={() => setFreeText({ ...freeText, [q.id]: true })}>Andere Antwort …</button>
          )}
        </div>
      ))}
      {error && <p className="error">{error}</p>}
      <button className="btn primary" disabled={send.isPending || Object.values(answers).every((a) => !a.trim())} onClick={() => send.mutate(answers)}>
        {send.isPending ? "Sende …" : "OK"}
      </button>
    </div>
  );
}
